"use client"

import type { I18nLocale } from "@/modules/i18n/types"
import type { AutocompleteSource, AutocompleteState } from "@algolia/autocomplete-core"
import type { Hit } from "@algolia/client-search"
import type { IconComponent } from "@jobteaser/spark/dist/types/Icon"
import type { SearchClient } from "algoliasearch/lite"
import type { ChangeEvent, FocusEvent, FunctionComponent, KeyboardEvent, MouseEvent } from "react"

import { createAutocomplete } from "@algolia/autocomplete-core"
import { getAlgoliaResults } from "@algolia/autocomplete-preset-algolia"
import { clsx } from "clsx"
import dynamic from "next/dynamic"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Button } from "@jobteaser/spark/components/Button"
import { IconButton } from "@jobteaser/spark/components/IconButton"
import { ChevronLeft } from "@jobteaser/spark/components/Icons/ChevronLeft"
import { Search } from "@jobteaser/spark/components/Icons/Search"
import { Text } from "@jobteaser/spark/components/Text"
import { TextInput } from "@jobteaser/spark/components/TextInput"

import { VISITOR_ID_COOKIE_NAME } from "@/modules/authentication/constants"
import { getCookie } from "@/modules/client/cookies/getCookies"
import { Highlight } from "@/modules/jobAds/client/components/AutocompleteSearchInput/Highlight"
import { AVAILABLE_AUTOCOMPLETE_INDEX_LOCALES } from "@/modules/jobAds/client/constants"
import { useJobAdsFilterNavigation } from "@/modules/jobAds/client/hooks/useJobAdsFilterNavigation"
import { useJobAdsSearchParams } from "@/modules/jobAds/client/hooks/useJobAdsSearchParams"
import { usePageContext } from "@/modules/jobSearch/components/PageContext/PageContextProvider"
import { getSearchEngine } from "@/modules/search/searchEngine"

import styles from "./AutocompleteSearchInput.module.css"

const SearchIcon: FunctionComponent = () => <Search width={20} height={20} />

type AutocompleteItem = Hit<{
  objectID: string
  query: string
}>

export type AutocompleteSearchProperties = {
  keywordSearched: string
  rankSuggestionClicked: number
  suggestionClicked: string
}

export type AutocompleteSearchInputProps = {
  careerCenterSuggestions?: string[]
  className?: string
  clearLabel: string
  inputContainerClassName?: string
  label: string
  locale: I18nLocale
  placeholder: string
  suggestionsTitle: string
}

type GetItemsFunction = (
  items: AutocompleteSource<AutocompleteItem>[]
) => Promise<AutocompleteSource<AutocompleteItem>[]>

const DynamicSavedSearch = dynamic(() =>
  import("@/modules/jobAds/client/components/SavedSearch/SavedSearch").then(mod => mod.SavedSearch)
)

const debouncePromise = (
  fn: (...args: Parameters<GetItemsFunction>) => ReturnType<GetItemsFunction>,
  time: number
): ((...args: Parameters<GetItemsFunction>) => ReturnType<GetItemsFunction>) => {
  let timerId: ReturnType<typeof setTimeout>

  return (...args: Parameters<GetItemsFunction>): ReturnType<GetItemsFunction> => {
    if (timerId) {
      clearTimeout(timerId)
    }

    return new Promise(resolve => {
      timerId = setTimeout(() => resolve(fn(...args)), time)
    })
  }
}

const debounced = debouncePromise(items => Promise.resolve(items), 250)

// we need to use a custom Search icon to avoid the default one from the TextInput (which is too small)
// eslint-disable-next-line react/no-multi-comp
export const AutocompleteSearchInput: FunctionComponent<AutocompleteSearchInputProps> = ({
  careerCenterSuggestions = [],
  className,
  inputContainerClassName,
  clearLabel,
  label,
  locale,
  placeholder,
  suggestionsTitle,
}) => {
  const autocompleteBaseIndexName = process.env.NEXT_PUBLIC_ALGOLIA_JOB_ADS_AUTOCOMPLETE_INDEX_NAME
  const computedAutocompleteIndexName = AVAILABLE_AUTOCOMPLETE_INDEX_LOCALES.includes(locale)
    ? `${autocompleteBaseIndexName}_${locale}`
    : autocompleteBaseIndexName

  const searchParams = useJobAdsSearchParams()
  const search = searchParams.toString()
  const qQuery = searchParams.get("q") || ""
  const { navigateWithUpdatedQueryParams } = useJobAdsFilterNavigation()
  const {
    isDirectAlgoliaConnectionEnabled,
    isSearchProxyV3ConnectionEnabled,
    isUserLogged,
    onSuggestionClicked,
    savedSearch,
  } = usePageContext()

  const [backdrop, setBackdrop] = useState(false)
  const [autocompleteState, setAutocompleteState] = useState<AutocompleteState<AutocompleteItem>>({
    activeItemId: null,
    collections: [],
    completion: null,
    context: {},
    isOpen: false,
    query: "",
    status: "idle",
  })
  const [searchEngine, setSearchEngine] = useState<SearchClient>()
  const [searchInputValue, setSearchInputValue] = useState(qQuery)

  const inputRef = useRef<HTMLInputElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const updateSearch = useMemo(
    () =>
      ({ value, isAnAutocompleteSearch }: { value: string; isAnAutocompleteSearch: boolean }): void => {
        navigateWithUpdatedQueryParams({
          // eslint-disable-next-line id-length
          availableFilters: { q: value },
          isAnAutocompleteSearch,
        })
      },
    // We remove "navigateWithUpdatedQueryParams" to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search]
  )

  const onStateChange = useCallback(
    (state: AutocompleteState<AutocompleteItem>): void => {
      if (searchInputValue && !state.query) {
        return
      }

      setAutocompleteState(state)
    },
    [searchInputValue]
  )

  const autocomplete = useMemo(
    () =>
      createAutocomplete<AutocompleteItem, React.BaseSyntheticEvent, React.MouseEvent, React.KeyboardEvent>({
        getSources() {
          if (!searchEngine) return []

          return debounced([
            {
              getItemInputValue({ item }) {
                return item.query
              },
              getItems({ query }) {
                return getAlgoliaResults({
                  queries: [
                    {
                      indexName: computedAutocompleteIndexName,
                      params: {
                        highlightPostTag: "</mark>",
                        highlightPreTag: "<mark>",
                        hitsPerPage: 5,
                      },
                      query,
                    },
                  ],
                  searchClient: searchEngine,
                })
              },
              sourceId: process.env.NEXT_PUBLIC_ALGOLIA_JOB_ADS_AUTOCOMPLETE_SOURCE_ID,
            },
          ])
        },
        insights: true,
        onStateChange({ state }) {
          if (searchEngine) {
            onStateChange(state)
          }
        },
      }),
    // Avoid infinite loop by removing "debounced" function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchEngine, onStateChange]
  )

  const { getEnvironmentProps } = autocomplete

  useEffect(() => {
    const init = async (): Promise<void> => {
      const algoliaSearchEngine = await getSearchEngine({
        isDirectAlgoliaConnectionEnabled,
        isMultiHostConnectionEnabled: false,
        isSearchProxyV3ConnectionEnabled,
        userToken: isUserLogged ? getCookie(VISITOR_ID_COOKIE_NAME) : null,
      })

      setSearchEngine(algoliaSearchEngine)
    }

    init()
  }, [isDirectAlgoliaConnectionEnabled, isSearchProxyV3ConnectionEnabled, isUserLogged])

  const inputProps = autocomplete.getInputProps({
    inputElement: inputRef.current,
  })

  const handleFieldChoice = useCallback(
    (value: string): void => {
      setBackdrop(false)
      document.body.classList.remove("app-Body--jobSearchAutoCompleteIsOpen")
      inputRef.current?.blur()
      updateSearch({
        isAnAutocompleteSearch: false,
        value,
      })
    },
    [updateSearch]
  )

  const handleSuggestionChoice = (suggestionValue?: string): void => {
    setBackdrop(false)
    document.body.classList.remove("app-Body--jobSearchAutoCompleteIsOpen")

    inputRef.current?.blur()
    updateSearch({
      isAnAutocompleteSearch: Boolean(suggestionValue),
      value: suggestionValue || searchInputValue,
    })
  }

  const handleInputFocus = (event: FocusEvent<HTMLInputElement>): void => {
    setBackdrop(true)
    document.body.classList.add("app-Body--jobSearchAutoCompleteIsOpen")
    inputProps.onFocus(event)
  }

  const handleInputClear = (): void => {
    autocomplete.setQuery("")
    autocomplete.refresh()
    setSearchInputValue("")
    updateSearch({
      isAnAutocompleteSearch: false,
      value: "",
    })
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.currentTarget
    setSearchInputValue(value)
    inputProps.onChange(event)
  }

  const handleBlur = useCallback((): void => {
    setBackdrop(false)
    document.body.classList.remove("app-Body--noScroll")
    document.body.classList.remove("app-Body--jobSearchAutoCompleteIsOpen")
  }, [])

  const handleInputKeydown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === "Enter") {
        handleFieldChoice(event.currentTarget.value)
      }

      if (event.key === "Escape") {
        handleFieldChoice(event.currentTarget.value)
      }

      if (
        ["ArrowDown", "Tab"].includes(event.key) &&
        !!autocompleteState.collections.length &&
        !!autocompleteState.collections[0].items.length
      ) {
        event.preventDefault()
        const element = document.getElementById(`job-ads-autocomplete-suggestion-0-0`)

        if (element) {
          element.focus()
        }
      }
    },
    [autocompleteState.collections, handleFieldChoice]
  )

  useEffect(() => {
    const currentInputRef = inputRef.current

    if (currentInputRef) {
      currentInputRef.addEventListener("keydown", handleInputKeydown as unknown as EventListener)
    }

    return () => {
      if (currentInputRef) {
        currentInputRef.removeEventListener("keydown", handleInputKeydown as unknown as EventListener)
      }
    }
  }, [handleInputKeydown])

  useEffect(() => {
    if (!formRef.current || !panelRef.current || !inputRef.current) {
      return undefined
    }

    const { onTouchStart, onTouchMove, onMouseDown } = getEnvironmentProps({
      formElement: formRef.current,
      inputElement: inputRef.current,
      panelElement: panelRef.current,
    })

    window.addEventListener("mousedown", onMouseDown)
    window.addEventListener("touchstart", onTouchStart)
    window.addEventListener("touchmove", onTouchMove)

    return () => {
      window.removeEventListener("mousedown", onMouseDown)
      window.removeEventListener("touchstart", onTouchStart)
      window.removeEventListener("touchmove", onTouchMove)
    }
  }, [getEnvironmentProps, autocompleteState.isOpen, handleInputKeydown])

  useEffect(() => {
    setSearchInputValue(searchParams.get("q") || "")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const isSavedSearchVisible = isUserLogged && !!savedSearch
  const showCareerCenterSuggestions = backdrop && !autocompleteState.isOpen && !!careerCenterSuggestions.length
  const showSavedSearch = backdrop && !autocompleteState.isOpen && isSavedSearchVisible
  const showAutocompleteList = backdrop && autocompleteState.isOpen

  if (inputProps.autoCapitalize === "off") {
    // The spec accepts "none" or "off" to disable the feature but Firefox seems to convert it to
    // "none" when it's set to "off" and it breaks the hydration.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    inputProps.autoCapitalize = "none"
  }

  return (
    <div
      {...autocomplete.getRootProps({})}
      aria-labelledby={undefined}
      className={clsx(className, styles.main, backdrop && styles.main___opened)}
    >
      {backdrop && (
        <div
          className={styles.backdrop}
          role="button"
          onClick={() => handleSuggestionChoice()}
          onKeyDown={() => handleSuggestionChoice()}
          tabIndex={0}
          aria-label="close"
        />
      )}
      <form {...autocomplete.getFormProps({ inputElement: inputRef.current })} ref={formRef} className={styles.form}>
        <IconButton
          className={styles.backButton}
          icon={ChevronLeft}
          onClick={() => {
            if (qQuery !== searchInputValue) {
              handleFieldChoice(searchInputValue)
            } else {
              handleBlur()
            }
          }}
        />

        <div className={styles.inputContainer}>
          <TextInput
            {...inputProps}
            className={styles.searchInput}
            clearIconAriaLabel={clearLabel}
            data-testid="job-ads-autocomplete-keyword-search-input"
            fieldClassName={clsx(styles.searchInputContainer, inputContainerClassName)}
            hasClearIcon
            id="job-ads-autocomplete-keyword-search"
            leftIcon={SearchIcon as IconComponent}
            onChange={handleInputChange}
            onClear={handleInputClear}
            onFocus={handleInputFocus}
            onKeyDown={handleInputKeydown}
            placeholder={placeholder}
            ref={inputRef}
            type="text"
            value={searchInputValue}
            label={label}
            hideLabel
          />
        </div>
      </form>

      {showCareerCenterSuggestions && (
        <div className={styles.careerCenterSuggestions}>
          <Text variant="body1" weight="semiBold" className={styles.title}>
            {suggestionsTitle}
          </Text>
          {careerCenterSuggestions.map(keyword => (
            <Button
              className={styles.suggestion}
              icon={Search}
              key={keyword}
              onClick={() => {
                handleSuggestionChoice(keyword)
              }}
              type="button"
              variant="tertiary"
            >
              {keyword}
            </Button>
          ))}
        </div>
      )}

      {showSavedSearch && <DynamicSavedSearch onClick={handleBlur} className={styles.savedSearchWrapper} />}

      {showAutocompleteList && (
        <div
          {...autocomplete.getPanelProps({})}
          ref={panelRef}
          className={styles.autocompletePanel}
          data-testid="autocomplete-panel"
        >
          {autocompleteState.collections.map((collection, collectionIndex) => {
            const { source, items } = collection
            const isFirstCollection = collectionIndex === 0
            const isLastCollection = collectionIndex + 1 === autocompleteState.collections.length

            return items.length > 0 ? (
              <section key={collection.source.sourceId}>
                <div {...autocomplete.getListProps()} aria-labelledby={undefined} className={styles.list}>
                  {items.map((item, index) => {
                    const itemProps = autocomplete.getItemProps({ item, source })
                    const id = `job-ads-autocomplete-suggestion-${collectionIndex}-${index}`
                    const isFirstItem = index === 0
                    const isLastItem = index + 1 === items.length

                    return (
                      <div
                        {...itemProps}
                        className={styles.suggestion}
                        id={id}
                        key={item.objectID}
                        onClick={(event: MouseEvent<HTMLDivElement>) => {
                          onSuggestionClicked?.({
                            keywordSearched: searchInputValue,
                            rankSuggestionClicked: index + 1,
                            suggestionClicked: item.query,
                          })
                          handleSuggestionChoice(item.query)
                          itemProps.onClick(event)
                        }}
                        onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                          if (event.key === "Escape") {
                            if (qQuery !== searchInputValue) {
                              handleFieldChoice(searchInputValue)
                            } else {
                              handleBlur()
                            }
                          } else if (event.key === "Tab" && isLastCollection && isLastItem) {
                            // As we will hide the suggestions, we break the tab order so we need to
                            // go out of the React lifecycle to avoid the re-focus on the closest
                            // focusable element which is the Input. This way, we let the focus go
                            // on the next focusable element before removing the suggestions.
                            setTimeout(() => {
                              handleSuggestionChoice()
                            }, 0)
                          } else if (event.key === "ArrowDown" && isLastCollection && isLastItem) {
                            // Go to the first suggestion
                            document.getElementById(`job-ads-autocomplete-suggestion-0-0`)?.focus()
                          } else if (event.key === "ArrowDown") {
                            // Go to the next suggestion
                            document
                              .getElementById(`job-ads-autocomplete-suggestion-${collectionIndex}-${index + 1}`)
                              ?.focus()
                          } else if (event.key === "ArrowUp" && isFirstCollection && isFirstItem) {
                            // Go to the last suggestion
                            const lastCollectionIndex = autocompleteState.collections.length - 1
                            const lastItemIndex = autocompleteState.collections[lastCollectionIndex].items.length - 1
                            document
                              .getElementById(`job-ads-autocomplete-suggestion-${lastCollectionIndex}-${lastItemIndex}`)
                              ?.focus()
                          } else if (event.key === "ArrowUp") {
                            // Go to the previous suggestion
                            document
                              .getElementById(`job-ads-autocomplete-suggestion-${collectionIndex}-${index - 1}`)
                              ?.focus()
                          } else if (event.key === "Enter") {
                            onSuggestionClicked?.({
                              keywordSearched: searchInputValue,
                              rankSuggestionClicked: index + 1,
                              suggestionClicked: item.query,
                            })
                            handleSuggestionChoice(item.query)
                            // Disguise the event as a KeyboardEvent to be accepted by the event handler
                            itemProps.onClick(event as unknown as MouseEvent<HTMLDivElement>)
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <Highlight hit={item} attribute="query" />
                      </div>
                    )
                  })}
                </div>
              </section>
            ) : null
          })}
        </div>
      )}
    </div>
  )
}
